import { useEffect, useState } from 'react'
import {
  api,
  USE_CASE_LABELS,
  type PageHealth,
  type Publication,
  type PulseSummary,
  type State,
  type Suggestion,
  type UseCase,
} from '../api'
import { Icon } from './Icon'
import { counted } from '../plural'
import { ago, titleOf } from '../pulse-format'

/**
 * The Pulse states, with the SHAPE that carries them where colour cannot.
 *
 * The same three glyphs the Pulse screen draws, for the same reason
 * `tokens.css` gives beside the ramp: filled, half and hollow read in
 * greyscale, to a colour-blind reader, and in a screenshot pasted into Slack.
 */
const MARK = { current: '\u25cf', review: '\u25d0', outdated: '\u25cb' } as const

/**
 * The first checklist step's hint, tuned by what the first run learned.
 *
 * The workspace said what its docs are FOR, and the least the product can do
 * is use the answer. The starter seed itself is not adapted — it is written
 * at provisioning time, before the question has been asked, so rewording it
 * afterwards would be rewriting content the person may already have opened.
 * The guidance adapts instead: same action, aimed at the page that kind of
 * documentation is actually judged by.
 */
/** What each kind of documentation is called in a sentence about it. */
const TEMPLATE_NAMES: Record<UseCase, string> = {
  product: 'product documentation site',
  api: 'API reference',
  help: 'help centre',
  handbook: 'handbook',
}

const WRITE_HINTS: Record<UseCase, string> = {
  product:
    'Open Pages and start a draft — a quick-start guide is the page product docs are judged by.',
  api: 'Open Pages and start a draft — authentication is the page an API reference earns trust on.',
  help: 'Open Pages and start a draft — answer the question your customers ask most.',
  handbook: 'Open Pages and start a draft — the page every new teammate needs on day one.',
}

/**
 * The landing screen: the pulse of the documentation, not an empty editor.
 *
 * The product's job is telling a team whether their docs still match reality.
 * Landing in an editor with nothing selected said the product's job was typing.
 * This screen answers the three questions somebody opens the app with — is
 * anything wrong, what changed, what should I do next — with the product's own
 * numbers, and says nothing it cannot back.
 */
export function Home({
  state,
  openFindings,
  team,
  siteName,
  useCase,
  onWrite,
  onOpenPage,
  onTemplate,
  onImport,
  onInbox,
  onPulse,
  onReview,
  onPeople,
  onSettings,
  onTour,
}: {
  state: State
  /**
   * The open inbox suggestions, and how many people are here — both already
   * fetched by the shell.
   *
   * Home used to ask for each again on arrival, so a cold load fetched the
   * inbox twice and the member list twice: two readers of one answer, each
   * asking for themselves. Null means the shell has not answered yet, which is
   * a different thing from an empty list and is why the tiles show a dash.
   */
  openFindings: Suggestion[] | null
  team: number | null
  /**
   * Which documentation site this is, by the name its people gave it.
   *
   * A workspace can hold several, and "Home" on its own answers the wrong
   * question. The line under the heading is the one place on the screen that
   * says where you are standing.
   */
  siteName: string | null
  /** What this workspace's docs are for, when the first run learned it. */
  useCase: UseCase | null
  onWrite: () => void
  /** Open a page in the editor — the rows on both cards below go there. */
  onOpenPage: (path: string) => void
  /** The health screen, which every state tile above is a door into. */
  onPulse: () => void
  /**
   * Walk this person around the writing screen.
   *
   * Offered at the foot of the checklist rather than sprung on arrival: the
   * tour that matters is about Pages, and Home is where somebody is deciding
   * what to do rather than doing it.
   */
  onTour?: () => void
  /** Writes the pages this kind of documentation needs onto a draft. */
  onTemplate: (useCase: UseCase | null) => void
  onImport: () => void
  onInbox: () => void
  onReview: () => void
  onPeople: () => void
  onSettings: () => void
}) {
  /*
   * From the shell, which already asked. The inbox and the member list were
   * each fetched twice on a cold load — once for the bell and the people
   * field, once here — for two readers of one answer.
   */
  const inboxOpen = openFindings === null ? null : openFindings.length
  const [domain, setDomain] = useState<string | null | undefined>(undefined)
  /**
   * `undefined` still reading, `null` nothing published, `'unavailable'` the
   * read failed — the third of which used to be written as the second, so a
   * server that could not answer told a customer their site had never been
   * published. Pulse already names an unreadable source this way.
   */
  const [lastPublish, setLastPublish] = useState<Publication | null | undefined | 'unavailable'>(
    undefined,
  )
  const [due, setDue] = useState<number | null>(null)
  /**
   * What Pulse says about every page, and the pages themselves.
   *
   * One request, read twice: the four tiles at the top are its summary, and
   * "Recently edited" is its `lastChanged` — which is git's answer rather than
   * a column somebody has to remember to write.
   */
  const [health, setHealth] = useState<{ summary: PulseSummary; pages: PageHealth[] } | null>(null)
  /** The open findings themselves, for the card that asks somebody to act. */
  const findings = openFindings ?? []
  const [readers, setReaders] = useState<{ pages: number; worst: string | null } | null>(null)
  const [gap, setGap] = useState<{ query: string; searches: number } | null | undefined>(undefined)
  /**
   * What this project's API description is saying, when it has one.
   *
   * Counted off the inbox rather than fetched again: Home already asks for it,
   * and the suggestions carry the trigger that says which came from a
   * specification.
   *
   * The SETTING is not the signal. A project does not have to name its file —
   * the engine finds one in the usual places — so `apiSpecPath` is null for
   * most projects that have an API description, and a card keyed on it would
   * be invisible exactly where it is useful. What proves a description exists
   * is that it produced something: an endpoint nobody documented, or a change
   * the pages have not caught up with. The setting still counts, so a project
   * that named its file and has nothing outstanding gets told so.
   */
  const apiDrift =
    openFindings === null
      ? null
      : {
          undocumented: openFindings.filter((one) => one.trigger === 'undocumented-api').length,
          changed: openFindings.filter((one) => one.trigger === 'api-change').length,
        }
  const [specPath, setSpecPath] = useState<string | null | undefined>(undefined)
  /**
   * The two warnings §8 asks a dashboard to carry: "billing/usage warnings
   * relevant to the project".
   *
   * RELEVANT is the qualifier that decides the shape: nothing is drawn unless
   * there is something to say. A payment that failed is a fact about the
   * workspace and cannot wait for somebody to open Settings; a day's allowance
   * four fifths spent is the moment before a writer meets a refusal.
   */
  const [spend, setSpend] = useState<Awaited<ReturnType<typeof api.aiUsage>> | null>(null)
  const [payment, setPayment] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    /**
     * What the AI features have spent today. Any member may read it — it is
     * spend, not a secret — and a writer about to lose the feature for the day
     * should see that coming.
     */
    void api
      .aiUsage()
      .then((r) => !cancelled && setSpend(r))
      .catch(() => !cancelled && setSpend(null))
    void api
      .publications()
      .then((r) => !cancelled && setLastPublish(r.publications[0] ?? null))
      .catch(() => !cancelled && setLastPublish('unavailable'))
    void api
      .workspaceSettings()
      .then((r) => {
        if (cancelled) return
        setDomain(r.domain)
        setSpecPath(r.apiSpecPath)
        setPayment(r.billingStatus ?? null)
      })
      .catch(() => {
        if (cancelled) return
        setDomain(null)
        setSpecPath(null)
      })
    /*
     * The Pulse counts, from the state the shell already loaded when it can be.
     *
     * `/api/state?include=health` answers them from the SAME read of the tree
     * it makes anyway; painting Home used to read the whole content tree twice,
     * a millisecond apart, for one tree. The shell asks for the include when
     * Home is the screen, which covers the load that matters — opening the app.
     *
     * The fallback is not defensive padding: arriving at Home from somewhere
     * else leaves the shell's state without the include, and asking then is
     * exactly right. Nothing is cached; both paths read a tree that was read
     * for this screen.
     */
    if (state.health) {
      setDue(state.health.summary.review + state.health.summary.outdated)
      setHealth({ summary: state.health.summary, pages: [] })
    } else {
      void api
        .health()
        .then((r) => {
          if (cancelled) return
          setDue(r.summary.review + r.summary.outdated)
          setHealth({ summary: r.summary, pages: r.pages })
        })
        .catch(() => {
          if (cancelled) return
          setDue(null)
          setHealth(null)
        })
    }
    void api
      .feedback()
      .then((r) => {
        if (cancelled) return
        const spoken = r.pages.filter((p) => p.helpful + p.notHelpful > 0)
        const worst = spoken.find((p) => p.notHelpful > p.helpful)
        setReaders({ pages: spoken.length, worst: worst?.path ?? null })
      })
      .catch(() => !cancelled && setReaders(null))
    void api
      .searchAnalytics()
      .then((r) => {
        if (cancelled) return
        // The most-searched query that found NOTHING. One, not a list: Home is
        // a place to notice something, and the screen behind it is where a
        // person works through the rest.
        const worst = r.queries.filter((q) => q.isGap).sort((a, b) => b.searches - a.searches)[0]
        setGap(worst ? { query: worst.query, searches: worst.searches } : null)
      })
      .catch(() => !cancelled && setGap(null))
    return () => {
      cancelled = true
    }
    // `state.health` is read above: when the shell asked for the include this
    // must re-run as the answer arrives, rather than falling back to a request
    // the shell has already made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.health])

  const pages = state.pages.length
  const drafts = state.branches.filter((b) => b.status !== 'merged' && b.status !== 'closed')

  /**
   * The line under the heading: where you are, how big it is, when readers
   * last saw a change.
   *
   * Three facts rather than a verdict about the lint, which is what used to be
   * here. "2 problems worth a look" is a sentence about a file nobody has
   * opened; "last published 2 days ago" is the thing somebody comes to this
   * screen to check, and the lint has a screen of its own to be judged on.
   */
  const published =
    lastPublish === undefined
      ? null
      : lastPublish === 'unavailable'
        ? 'last publish could not be read'
        : lastPublish === null
          ? 'never published'
          : `last published ${ago(lastPublish.createdAt) ?? 'recently'}`

  return (
    <div className="screen">
      <header className="screen__head">
        <h1>Home</h1>
        <p className="screen__sub">
          {[siteName, `${pages} ${pages === 1 ? 'page' : 'pages'}`, published]
            .filter(Boolean)
            .join(' \u00b7 ')}
        </p>
      </header>

      {/*
        What Pulse says, in four numbers, before anything asks for a decision.

        These replace a row that counted pages, suggestions, drafts and lint
        problems — four numbers about four unrelated things, two of which are
        now said better elsewhere: the drafts are the count in the top bar's
        own pill, and the lint problems are what the publish sheet refuses on.
        These four are one question asked four ways, which is what a row of
        tiles is for.

        Each carries its Pulse SHAPE as well as its colour, for the reason
        `tokens.css` states beside the ramp: it has to read in greyscale.
      */}
      <div className="stats">
        <button className="stat stat--action" onClick={onPulse} data-stat="current">
          <b className={health?.summary.current ? 'stat--current' : ''}>
            <span className="pulse-mark pulse-mark--current" aria-hidden="true">
              {MARK.current}
            </span>
            {health ? health.summary.current : '\u2013'}
          </b>
          <span>pages current</span>
        </button>
        <button className="stat stat--action" onClick={onPulse} data-stat="review">
          <b className={health?.summary.review ? 'stat--review' : ''}>
            <span className="pulse-mark pulse-mark--review" aria-hidden="true">
              {MARK.review}
            </span>
            {health ? health.summary.review : '\u2013'}
          </b>
          <span>{health?.summary.review === 1 ? 'needs a recheck' : 'need a recheck'}</span>
        </button>
        <button className="stat stat--action" onClick={onPulse} data-stat="outdated">
          <b className={health?.summary.outdated ? 'stat--outdated' : ''}>
            <span className="pulse-mark pulse-mark--outdated" aria-hidden="true">
              {MARK.outdated}
            </span>
            {health ? health.summary.outdated : '\u2013'}
          </b>
          <span>outdated</span>
        </button>
        <button className="stat stat--action" onClick={onInbox} data-stat="inbox">
          <b>{inboxOpen ?? '\u2013'}</b>
          <span>{counted(inboxOpen ?? 0, 'inbox suggestion', 'inbox suggestions')}</span>
        </button>
      </div>

      {(() => {
        /**
         * The checklist, computed from what is actually true — never ticked by
         * hoping. Each step is one action, and the whole card leaves when the
         * work is done: a finished checklist that stays is a nag.
         */
        /** One step of the checklist: an action, or a question with answers. */
        interface Step {
          id: string
          label: string
          done: boolean
          hint: string
          act?: () => void
          /**
           * What the BUTTON says, when the step's name is not what pressing it
           * does. "Publish for the first time" is the step; "Publish 7
           * changes" is the act, and a button that says the step reads as a
           * heading somebody has to guess is clickable.
           */
          cta?: string
          /** When the step cannot act until somebody answers something. */
          choices?: { label: string; act: () => void }[]
        }
        const steps: Step[] = [
          {
            id: 'write',
            label: 'Write your first page',
            done: drafts.length > 0 || state.staged.length > 0,
            act: onWrite,
            cta: 'Open Pages',
            hint: useCase
              ? WRITE_HINTS[useCase]
              : 'Open Pages and start a draft — the live site never changes until you publish.',
          },
          {
            /**
             * The other way to start, for somebody who skipped the first run —
             * where this is also offered. A capability with one door, on one
             * screen, that appears once is one most people never meet.
             *
             * Done once there is more here than the page every workspace
             * starts with: a site with pages in it does not need a template,
             * and a checklist that goes on asking is a nag.
             */
            id: 'template',
            label: 'Start from a template',
            done: state.pages.length > 1 || drafts.length > 0,
            /*
             * With no answer to "what are these docs for", the door asks —
             * because the API cannot guess and will not pretend to: it answers
             * "say what these docs are for first — the first-run question sets
             * it", and for somebody who skipped that screen the first-run
             * question is exactly what is no longer on offer. A door onto a
             * wall, found by a gate pressing it.
             */
            ...(useCase
              ? { act: () => onTemplate(useCase) }
              : {
                  choices: (['product', 'api', 'help', 'handbook'] as UseCase[]).map((kind) => ({
                    label: USE_CASE_LABELS[kind],
                    act: () => onTemplate(kind),
                  })),
                }),
            hint: useCase
              ? `The pages a ${TEMPLATE_NAMES[useCase]} needs, on a draft — yours to rewrite.`
              : 'The pages this kind of documentation needs, on a draft — yours to rewrite.',
          },
          {
            id: 'import',
            label: 'Bring your existing docs',
            done: state.branches.some((b) => b.name.startsWith('draft/import-')),
            act: onImport,
            cta: 'Import a site',
            hint: 'GitBook, Docusaurus, Mintlify or a Markdown folder, onto a draft with a report.',
          },
          {
            id: 'publish',
            label: 'Publish for the first time',
            done: lastPublish != null && lastPublish !== 'unavailable',
            act: onReview,
            cta:
              state.staged.length > 0
                ? `Publish ${state.staged.length} ${counted(state.staged.length, 'change', 'changes')}`
                : 'Publish',
            hint: 'Readers see it about a minute after you press Publish.',
          },
          {
            id: 'team',
            label: 'Invite your team',
            done: (team ?? 0) > 1,
            act: onPeople,
            cta: 'Invite somebody',
            hint: 'Writers write on drafts; you decide what publishes.',
          },
          {
            id: 'domain',
            label: 'Put it on your domain',
            done: Boolean(domain),
            act: onSettings,
            cta: 'Connect a domain',
            hint: 'Each docs site answers on its own hostname \u2014 we show you the two DNS records.',
          },
        ]
        const open = steps.filter((s) => !s.done)
        if (open.length === 0 || team === null) return null
        const done = steps.length - open.length
        return (
          <section className="home__card home__card--wide checklist__card">
            {/*
              The heading, the count and the walk, on one line.

              The tour used to be a sentence at the FOOT of the list — under
              six steps, which is under the fold on a laptop, which is where
              somebody who does not know where anything is will never look for
              it. It belongs beside the thing it is an alternative to.
            */}
            <div className="checklist__head">
              <div>
                <h2>Set up {siteName ?? 'your documentation'}</h2>
                <p className="home__quiet">
                  {done} of {steps.length} done
                </p>
              </div>
              {onTour && (
                <button className="btn btn--sm" onClick={onTour}>
                  <Icon name="eye" size={13} />
                  Take the 2-minute tour
                </button>
              )}
            </div>
            {/*
              A bar as well as the count.
              
              "2 of 5" is a fact somebody has to do arithmetic on; a bar is
              the same fact at a glance, and it is the difference between a
              list that feels finishable and one that feels like homework.
              `aria-hidden`, because the heading beside it already says the
              numbers out loud and hearing them twice is worse than once.
            */}
            <div className="progress" aria-hidden="true">
              <span
                className="progress__done"
                style={{ width: `${Math.round((done / Math.max(1, steps.length)) * 100)}%` }}
              />
            </div>
            {/*
              A row per step: the mark, what it is and why, and the act itself
              on the right — where the eye ends up rather than where it starts.

              The FIRST unfinished step carries the screen's one filled button.
              A checklist whose actions are all the same weight is a list of
              six equal demands; naming the next one is the whole difference
              between a checklist and a nag, and BRAND.md 13 allows exactly one
              per screen, which is the same rule read from the other side.
            */}
            <ul className="checklist">
              {steps.map((step) => {
                const next = !step.done && step.id === open[0]?.id
                return (
                  <li
                    key={step.id}
                    className={
                      step.done ? 'checklist__item checklist__item--done' : 'checklist__item'
                    }
                  >
                    <span className="checklist__mark">
                      <Icon name={step.done ? 'check' : 'chevron-right'} size={12} />
                    </span>
                    <span className="checklist__body">
                      <span className="checklist__label">{step.label}</span>
                      {!step.done && <span className="checklist__hint">{step.hint}</span>}
                    </span>
                    {!step.done && step.choices && (
                      /*
                       * A step that cannot act until somebody answers
                       * something. The answers are the buttons: one press, not
                       * a press that opens a form.
                       */
                      <span className="checklist__ask">
                        {step.choices.map((choice) => (
                          <button
                            key={choice.label}
                            className="btn btn--ghost btn--sm"
                            onClick={choice.act}
                          >
                            {choice.label}
                          </button>
                        ))}
                      </span>
                    )}
                    {!step.done && !step.choices && step.act && (
                      <button
                        className={`btn btn--sm${next ? ' btn--primary' : ''} checklist__act`}
                        onClick={step.act}
                      >
                        {step.cta ?? step.label}
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })()}

      {(() => {
        /**
         * Drafts somebody is waiting on. `requested` waits on a reviewer;
         * `changes_requested` waits on its author. Both are a person blocked
         * on a person, which is exactly what a home screen should surface —
         * and the card leaves when nobody is waiting.
         */
        const waiting = drafts.filter(
          (b) => b.reviewStatus === 'requested' || b.reviewStatus === 'changes_requested',
        )
        if (waiting.length === 0) return null
        return (
          <section className="home__card home__card--wide">
            <h2>In review</h2>
            <ul className="reviewlist">
              {waiting.map((b) => (
                <li key={b.name} className="reviewlist__row">
                  <span className="mono truncate">{b.name.replace(/^draft\//, '')}</span>
                  <span
                    className={`badge review-badge review-badge--${b.reviewStatus ?? 'requested'}`}
                  >
                    {b.reviewStatus === 'requested' ? 'Waiting on a reviewer' : 'Changes asked for'}
                  </span>
                </li>
              ))}
            </ul>
            <p className="home__quiet">
              Open the draft and its Review panel to read the conversation, judge the change, or
              answer.
            </p>
          </section>
        )
      })()}

      {(() => {
        /**
         * The warnings, and nothing when there are none (§8).
         *
         * Two kinds, and both are things somebody has to act on rather than
         * numbers to admire: a payment that did not go through, and a day's
         * allowance nearly spent. The threshold is the same four fifths the
         * notice uses, so the card and the message cannot disagree about when
         * "nearly" begins.
         */
        const WARN_AT = 0.8
        const lines: string[] = []
        if (payment === 'past_due') {
          lines.push(
            'A payment did not go through. Stripe is trying again, and nothing is switched off — the card is in Settings.',
          )
        }
        const near = (used: number, limit: number) =>
          limit > 0 && used >= Math.floor(limit * WARN_AT)
        if (
          spend?.configured &&
          spend.writing.on &&
          near(spend.writing.project.used, spend.writing.project.limit)
        ) {
          lines.push(
            `Help with the words has used ${spend.writing.project.used} of today’s ${spend.writing.project.limit} for this site.`,
          )
        }
        if (spend?.configured && spend.ask.on && near(spend.ask.site.used, spend.ask.site.limit)) {
          lines.push(
            `Readers have asked ${spend.ask.site.used} of today’s ${spend.ask.site.limit} questions.`,
          )
        }
        if (lines.length === 0) return null
        return (
          <section className="home__card home__card--wide" data-home-warnings>
            <h2>Worth knowing</h2>
            <ul className="reviewlist">
              {lines.map((line) => (
                <li key={line} className="reviewlist__row">
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <p className="home__quiet">
              <button className="btn btn--ghost btn--sm" onClick={onSettings}>
                Open Settings
              </button>
            </p>
          </section>
        )
      })()}

      {/*
        Two cards, and both are lists of things with a name on them.

        What was here — Start, Published, Readers — was three cards of STATUS
        with a door each: a Write button that repeated the checklist's first
        step, a paragraph naming the last publication, and this week's view
        count. None of it asked anybody to do anything, and two of the three
        are now said better elsewhere: the last publication is the line under
        the heading, with the whole story in History, and the reader numbers
        are a screen of their own with a month of context on it.

        These two answer the questions a person actually opens this screen
        with. What needs me? And what did we touch last?
      */}
      <div className="home__row home__row--two">
        {(() => {
          /**
           * Everything with somebody's name on it, in one list.
           *
           * Three sources, one shape: a Pulse finding (what changed around a
           * page), a question readers asked that no page answers, and a page
           * whose freshness has run out. Each row is one sentence, one line of
           * evidence, and ONE act — because a row that offers three is a row
           * somebody reads twice and leaves.
           *
           * Capped at four. This is a glance; the Inbox and Pulse are where
           * the rest of the work is, and both are one click from the tiles
           * above.
           */
          interface Row {
            key: string
            mark: React.ReactNode
            says: string
            why: string
            act: { label: string; run: () => void }
          }
          const rows: Row[] = []
          for (const finding of findings.slice(0, 2)) {
            rows.push({
              key: `finding-${finding.id}`,
              mark: (
                <span className="pulse-mark pulse-mark--review" aria-hidden="true">
                  {MARK.review}
                </span>
              ),
              says: finding.title,
              why: finding.detail || 'Pulse noticed this while reading the pages.',
              act: { label: 'Open', run: onInbox },
            })
          }
          if (gap) {
            rows.push({
              key: 'gap',
              mark: <Icon name="search" size={13} />,
              says: `Readers searched for \u201c${gap.query}\u201d and found nothing`,
              why: `${gap.searches} ${counted(gap.searches, 'search', 'searches')} in the last month, and no page answers it`,
              act: { label: 'Write the page', run: onWrite },
            })
          }
          if (readers?.worst) {
            const worst = readers.worst
            rows.push({
              key: 'unhelpful',
              mark: <Icon name="people" size={13} />,
              says: `Readers said ${titleOf(state, worst)} did not help`,
              why: 'More of them pressed no than yes on this page',
              act: { label: 'Open the page', run: () => onOpenPage(worst) },
            })
          }
          for (const page of (health?.pages ?? [])
            .filter((p) => p.state === 'outdated')
            .slice(0, 2)) {
            rows.push({
              key: `stale-${page.path}`,
              mark: (
                <span className="pulse-mark pulse-mark--outdated" aria-hidden="true">
                  {MARK.outdated}
                </span>
              ),
              says: `${titleOf(state, page.path)} is past its recheck date`,
              why: page.lastReviewed
                ? `Last checked ${ago(page.lastReviewed)}`
                : 'Nobody has ever marked it checked',
              act: { label: 'Open the page', run: () => onOpenPage(page.path) },
            })
          }
          return (
            <section className="home__card" data-home-attention>
              <h2>Needs your attention</h2>
              {rows.length === 0 ? (
                <p className="nothing">
                  {health || findings.length > 0
                    ? 'Nothing is waiting on you. Pulse says so here when something changes around a page, or when readers cannot find one.'
                    : 'Still reading what Pulse and your readers have to say.'}
                </p>
              ) : (
                <ul className="attention">
                  {rows.slice(0, 4).map((row) => (
                    <li className="attention__row" key={row.key}>
                      <span className="attention__mark">{row.mark}</span>
                      <span className="attention__body">
                        <span className="attention__says">{row.says}</span>
                        <span className="attention__why">{row.why}</span>
                      </span>
                      <button className="btn btn--sm" onClick={row.act.run}>
                        {row.act.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )
        })()}

        {(() => {
          /**
           * What was touched last, from git's own dates.
           *
           * Pulse already asks the repository when every page last changed —
           * that is the number the whole product is named after — so this is a
           * second reading of a fact already fetched rather than a new one to
           * keep in step. Pages with no date at all are left out: "age
           * unknown" is honest on the Pulse screen, where the row is about the
           * page's health, and noise in a list whose only job is recency.
           */
          const recent = (health?.pages ?? [])
            .filter((page) => page.lastChanged)
            .sort((a, b) => (a.lastChanged! < b.lastChanged! ? 1 : -1))
            .slice(0, 5)
          return (
            <section className="home__card" data-home-recent>
              <h2>Recently edited</h2>
              {health === null ? (
                <span className="skeleton skeleton--line" />
              ) : recent.length === 0 ? (
                <p className="nothing">
                  Nothing has been edited yet \u2014 or the repository has no history to read the
                  dates from.
                </p>
              ) : (
                <ul className="recent">
                  {recent.map((page) => (
                    <li key={page.path}>
                      <button className="recent__row" onClick={() => onOpenPage(page.path)}>
                        <Icon name="page" size={13} />
                        <span className="recent__title truncate">{titleOf(state, page.path)}</span>
                        <span className="recent__when">{ago(page.lastChanged)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )
        })()}

        {apiDrift && (Boolean(specPath) || apiDrift.undocumented + apiDrift.changed > 0) && (
          <section className="home__card" data-api-state>
            <h2>API</h2>
            {apiDrift.undocumented === 0 && apiDrift.changed === 0 ? (
              <p className="home__quiet">
                Every endpoint in <b>{specPath}</b> has a page, and nothing has changed since the
                last publish.
              </p>
            ) : (
              <p>
                {apiDrift.undocumented > 0 && (
                  <>
                    <b>{apiDrift.undocumented}</b>{' '}
                    <span className="home__quiet">
                      endpoint{apiDrift.undocumented === 1 ? '' : 's'} nobody has documented
                    </span>
                  </>
                )}
                {apiDrift.undocumented > 0 && apiDrift.changed > 0 && (
                  <span className="home__quiet">, and </span>
                )}
                {apiDrift.changed > 0 && (
                  <>
                    <b>{apiDrift.changed}</b>{' '}
                    <span className="home__quiet">
                      change{apiDrift.changed === 1 ? '' : 's'} the pages have not caught up with
                    </span>
                  </>
                )}
              </p>
            )}
            <button className="btn" onClick={onInbox}>
              <Icon name="alert" size={13} /> Open the inbox
            </button>
          </section>
        )}
      </div>
    </div>
  )
}
